import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Invoice } from 'src/app/shared/models/invoice.interface';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { ShipService } from 'src/app/services/ship/ship.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PaginatedResponse } from 'src/app/shared/models';
import { InvoiceService } from 'src/app/services/invoice/invoice.service';
import { BankService } from 'src/app/services/bank/bank.service';
import { UserService } from 'src/app/services/user/user.service';
import { User } from 'src/app/shared/models/user.interface';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-invoice-detail-modal',
  templateUrl: './invoice-detail-modal.component.html',
  styleUrls: ['./invoice-detail-modal.component.scss']
})
export class InvoiceDetailModalComponent extends DestructorComponent implements OnInit, OnChanges {
  @Input() invoice: Invoice | null = null;
  @Input() invoiceId: string = '';
  @Output() invoiceCancelled = new EventEmitter<void>();

  // Form properties
  invoiceForm: FormGroup;
  isLoading: boolean = false;

  selectedInvoice: Invoice | null = null;

  // Ship search properties
  shipSearchTerm: string = ''; // Initialize with default ship name
  shipSearchResults: any[] = [];
  isShipSearching: boolean = false;
  showShipDropdown: boolean = false;
  selectedShip: any = null;
  private shipSearchSubject = new Subject<string>();
  banks: any[] = [];

  // User search properties for Consignee
  userSearchTerm: string = '';
  userSearchResults: User[] = [];
  isUserSearching: boolean = false;
  showUserDropdown: boolean = false;
  selectedUser: User | null = null;
  private userSearchSubject = new Subject<string>();

  // Tooltip properties
  currentTooltip: HTMLElement | null = null;

  // Stock number search properties
  stockNumberSearch: string = '';
  isSearchingInvoice: boolean = false;

  constructor(
    private fb: FormBuilder,
    private _shipService: ShipService,
    private _invoiceService: InvoiceService,
    private _bankService: BankService,
    private _userService: UserService
  ) {
    super();
    this.setupShipSearchDebounce();
    this.setupUserSearchDebounce();

  }

  ngOnInit(): void {
    // this.populateFormWithStaticData();
    this.getBanks();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Log when invoice data changes (more reliable for modal reuse)
    if (changes['invoice'] && changes['invoice'].currentValue) {
      this.selectedInvoice = changes['invoice'].currentValue;
      this.initializeForm();
    }

  }

  // Static form data as requested
  staticData = {
    stockNumber: 'TSL28465',
    invoiceDate: '2025-10-04',
    invoiceAmount: '2,127,000',
    approvedBy: 'Hamza Saleem',
    consignee: 'MTRL KINSEY MTRL KINSEY Cyprus',
    consigneeName: 'Kirill Kinsey',
    consigneeEmail: 'kirill.kinsey@gmail.com',
    consigneePhone: '+35799666266',
    chassisNo: 'FE12-298436',
    engineNumber: 'DA AT SIGHT',
    engineType: 'DA AT SIGHT',
    payTerms: 'DA AT SIGHT',
    insurance: '2,127,000',
    freight: '',
    cifAmount: '2,127,000',
    bank: 'MIZUHO BANK LT',
    paymentDueDate: '',
    notifyParty: 'Same As Consignee',
    notifyPartyCompany: '',
    notifyPartyName: '',
    notifyPartyEmail: '',
    notifyPartyPhone: '',
    notifyPartyAddress: '',
    notifyPartyCity: '',
    carrierName: '',
    voyageNumber: '01514',
    sailingDate: '2025-10-13',
    sailingPort: 'Yokohama',
    destinationPort: 'Auckland',
    destinationCountry: 'New Zealand',
    arrivalDate: '2025-11-08',
    shipName: 'GALAXY',
    ageName: 'MOL',
    totalCost: '1,817,600',
    salesPrice: '',
    coolerName: '',
    coolerNo: '',
    countryNo: '',
    profitLoss: 'Profit Loss',
    buyerComment: 'PLEASE INCLUDE PROFORMA NUMBER IN THE REMITTANCE INFORMATION SECTION WHEN MAKING YOUR PAYMENT',
    sellerComment: 'We Buyer should bear the cost of Bank Charge when remitting. We certify that the goods are of Japanese origin'
  };

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatCurrency(amount: number | undefined, currency: string | undefined = 'USD'): string {
    if (!amount) return '-';
    return `${amount.toLocaleString()} ${currency || 'USD'}`;
  }

  getBanks() {
    this.subscriptions.push(
      this._bankService.GetData("1", "", { paginate: false }).subscribe(res => {
        this.banks = res.data || [];
      })
    );
  }

  // Check if invoice has allocations
  hasAllocations(): boolean {
    return !!(this.selectedInvoice?.allocations && this.selectedInvoice.allocations.length > 0);
  }

  // Form initialization and management
  initializeForm(): void {
    // Check if invoice has allocations
    // If invoice has allocations, disable the fields (not editable)
    // If invoice has no allocations, enable the fields (editable)
    const hasAllocations = this.hasAllocations();
    const shouldDisableConsigneeFields = hasAllocations;

    this.invoiceForm = this.fb.group({
      // Basic Information
      stockNumberSearch: [''], // Editable search field
      stockNumber: [{ value: this.selectedInvoice?.vehicle?.car_no || '', disabled: true }, [Validators.required]],
      approvedDate: [{ value: this.formatDate(this.selectedInvoice?.created_at) || '-', disabled: true }],
      invoiceDate: [{ value: this.formatDate(this.selectedInvoice?.created_at) || '', disabled: true }, [Validators.required]],
      invoiceAmount: [{ value: this.formatCurrency(this.selectedInvoice?.invoice_amount, this.selectedInvoice?.item?.currency), disabled: true }, [Validators.required]],
      approvedBy: [this.selectedInvoice?.created_by?.name || ''],
      invoiceNo: [{ value: this.selectedInvoice?.invoice_number, disabled: true }],
      chassisNo: [{ value: this.selectedInvoice?.vehicle?.vin_no, disabled: true }],
      lotCode: [{ value: this.selectedInvoice?.vehicle?.lot_no, disabled: true }],
      engineNumber: [{ value: this.selectedInvoice?.vehicle?.engine_no, disabled: true }],
      engineType: [''],
      payTerms: [{ value: this.selectedInvoice?.order?.payment_terms, disabled: true }],

      // Consignee Information - Disabled if no allocations
      consigneeId: [{ value: this.selectedInvoice?.order?.buyer_id || '', disabled: shouldDisableConsigneeFields }],
      consignee: [{ value: this.selectedInvoice?.order?.buyer_company_name || '', disabled: shouldDisableConsigneeFields }],
      consigneeName: [{ value: this.selectedInvoice?.order?.buyer_consignee_name || '', disabled: shouldDisableConsigneeFields }],
      consigneeEmail: [{ value: this.selectedInvoice?.order?.buyer_email_address || '', disabled: shouldDisableConsigneeFields }, [Validators.email]],
      consigneePhone: [{ value: this.selectedInvoice?.order?.buyer_telephone_no || '', disabled: shouldDisableConsigneeFields }],
      consigneeAddress: [{ value: this.selectedInvoice?.order?.buyer_street_address || '', disabled: shouldDisableConsigneeFields }],
      consigneeCity: [{ value: this.selectedInvoice?.order.buyer_city || '', disabled: shouldDisableConsigneeFields }],

      // Notify Party Information - Disabled if no allocations
      sameAsConsignee: [{ value: this.selectedInvoice?.order.same_as_consignee || false, disabled: shouldDisableConsigneeFields }],
      notifyParty: [{ value: '', disabled: shouldDisableConsigneeFields }],
      notifyPartyCompany: [{ value: this.selectedInvoice?.order.same_as_consignee ? this.selectedInvoice?.order?.buyer_company_name : this.selectedInvoice?.order?.notify_party_company || '', disabled: shouldDisableConsigneeFields }],
      notifyPartyName: [{ value: this.selectedInvoice?.order.same_as_consignee ? this.selectedInvoice?.order?.buyer_consignee_name : this.selectedInvoice?.order?.notify_party_name || '', disabled: shouldDisableConsigneeFields }],
      notifyPartyEmail: [{ value: this.selectedInvoice?.order.same_as_consignee ? this.selectedInvoice?.order?.buyer_email_address : this.selectedInvoice?.order?.notify_party_email || '', disabled: shouldDisableConsigneeFields }, [Validators.email]],
      notifyPartyPhone: [{ value: this.selectedInvoice?.order.same_as_consignee ? this.selectedInvoice?.order?.buyer_telephone_no : this.selectedInvoice?.order?.notify_party_telephone || '', disabled: shouldDisableConsigneeFields }],
      notifyPartyAddress: [{ value: this.selectedInvoice?.order.same_as_consignee ? this.selectedInvoice?.order?.buyer_street_address : this.selectedInvoice?.order?.notify_party_address || '', disabled: shouldDisableConsigneeFields }],
      notifyPartyCity: [{ value: this.selectedInvoice?.order.same_as_consignee ? this.selectedInvoice?.order.buyer_city : this.selectedInvoice?.order?.notify_party_city || '', disabled: shouldDisableConsigneeFields }],

      // Financial Information
      totalCost: [this.selectedInvoice?.vehicle?.actual_cost_jpy || ''],
      salesPrice: [this.selectedInvoice?.vehicle?.sales_cost || ''],
      coolerName: [''],
      coolerNo: [''],
      insurance: [this.selectedInvoice?.vehicle?.car_insurance_refund || ''],
      freight: [this.selectedInvoice?.vehicle?.ocean_freight || ''],
      cifAmount: [this.selectedInvoice?.cif_amount || ''],
      bank: [this.selectedInvoice?.bank || ''],
      paymentDueDate: [this.selectedInvoice?.payment_due_date || ''],

      // Shipping Information
      ship_id: [this.selectedInvoice?.ship_id || ''],
      shipName: [this.selectedInvoice?.ship?.name || ''],
      ageName: [''],
      carrierName: [this.selectedInvoice?.ship?.company || ''],
      voyageNumber: [this.selectedInvoice?.ship?.voyage_no || ''],
      sailingDate: [this.selectedInvoice?.ship?.sailing_date || ''],
      sailingPort: [this.selectedInvoice?.ship?.sailing_port || ''],
      destinationPort: [this.selectedInvoice?.ship?.destination_port || ''],
      destinationCountry: [this.selectedInvoice?.ship?.destination_country || ''],
      arrivalDate: [this.selectedInvoice?.ship?.arrival_date || ''],

      // Comments
      notes: [this.selectedInvoice?.notes || ''],
      adminComments: [this.selectedInvoice?.admin_comments || '']
    });

    // Add listener for "Same as Consignee" checkbox
    this.invoiceForm.get('sameAsConsignee')?.valueChanges.subscribe(() => {
      this.onSameAsConsigneeChange();
    });

    // Set the shipSearchTerm to display the ship name in the search field
    this.shipSearchTerm = this.selectedInvoice?.ship?.name || '';
  }

  onSave(): void {
    if (this.invoiceForm.valid) {
      const formData = this.invoiceForm.getRawValue();

      this.isLoading = true;

      let submitData:any = {
        ship_id: formData.ship_id,
        // consignee_id: formData.consigneeId,
        admin_comments: formData.adminComments,
        notes: formData.notes,
        vehicle: {
          total_cost: formData.totalCost ? +formData.totalCost : null,
          sales_price: formData.salesPrice ? +formData.salesPrice : null,
          insurance: formData.insurance ? +formData.insurance : null,
          ocean_freight: formData.freight ? +formData.freight : null
        },
        cif_amount: +formData.cifAmount,
        bank: formData.bank,
        payment_due_date: formData.paymentDueDate,
      };

      if (this.selectedInvoice?.allocations && !this.selectedInvoice.allocations.length){

        submitData = {
          ...submitData,
          "order.buyer_consignee_name": formData.consigneeName,
          "order.buyer_street_address": formData.consigneeAddress,
          "order.buyer_city": formData.consigneeCity,
          "order.buyer_email_address": formData.consigneeEmail,
          "order.buyer_telephone_no": formData.consigneePhone,
          "order.buyer_company_name": formData.consignee,
          "order.buyer_country": formData.consigneeCountry,
          "order.same_as_consignee": formData.sameAsConsignee,
          "order.notify_party_name": formData.notifyPartyName,
          "order.notify_party_company": formData.notifyPartyCompany,
          "order.notify_party_email": formData.notifyPartyEmail,
          "order.notify_party_telephone": formData.notifyPartyPhone,
          "order.notify_party_address": formData.notifyPartyAddress,
          "order.notify_party_city": formData.notifyPartyCity,
          "order.buyer_id": formData.consigneeId,
          "order.notify_party_country": formData.notifyPartyCountry
        }

      }

      console.log({ formData })

      this._invoiceService.UpdateData(this.selectedInvoice?._id || "", submitData).subscribe({
        next: (response) => {
          this.closeModal();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error updating invoice:', error);
          this.isLoading = false;
        }
      })
      // You can add additional processing here
      // For example: API call to save the data

    } else {
      console.log('Form is invalid');
      this.markFormGroupTouched(this.invoiceForm);
    }
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_invoice_detail');
    if (modalElement) {
      const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Ship search methods
  private setupShipSearchDebounce(): void {
    this.shipSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchShips(searchTerm);
    });
  }

  onShipSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.shipSearchTerm = target.value;
    this.selectedShip = null;

    this.shipSearchSubject.next(this.shipSearchTerm);
    this.showShipDropdown = true;
  }

  private searchShips(searchTerm: string): void {
    this.isShipSearching = true;
    this.subscriptions.push(
      this._shipService.GetData('1', searchTerm).subscribe({
        next: (response: PaginatedResponse<any>) => {
          this.isShipSearching = false;
          if (response.status && response.data && response.data.data) {
            this.shipSearchResults = response.data.data;
            this.showShipDropdown = this.shipSearchResults.length > 0;
          } else {
            this.shipSearchResults = [];
            this.showShipDropdown = false;
          }
        },
        error: (error: any) => {
          this.isShipSearching = false;
          console.error('Error searching ships:', error);
          this.shipSearchResults = [];
          this.showShipDropdown = false;
        }
      })
    );
  }

  selectShip(ship: any): void {
    this.selectedShip = ship;
    this.shipSearchTerm = ship.name; // This will show the selected ship name in the search input
    this.showShipDropdown = false;
    this.shipSearchResults = [];

    // Update form with selected ship information
    this.invoiceForm.patchValue({
      ship_id: ship._id,
      shipName: ship.name,
      ageName: ship.company,
      voyageNumber: ship.voyage_no,
      sailingDate: this.formatDate(ship.sailing_date),
      sailingPort: ship.sailing_port,
      destinationPort: ship.destination_port,
      destinationCountry: ship.destination_country,
      arrivalDate: this.formatDate(ship.arrival_date)
    });

    // Also update static data for backward compatibility
    this.staticData.shipName = ship.name;
    this.staticData.ageName = ship.company;
    this.staticData.voyageNumber = ship.voyage_no;
    this.staticData.sailingDate = this.formatDate(ship.sailing_date);
    this.staticData.sailingPort = ship.sailing_port;
    this.staticData.destinationPort = ship.destination_port;
    this.staticData.destinationCountry = ship.destination_country;
    this.staticData.arrivalDate = this.formatDate(ship.arrival_date);
  }

  clearShipSearch(): void {
    this.shipSearchTerm = ''; // This will clear the search input
    this.selectedShip = null;
    this.showShipDropdown = false;
    this.shipSearchResults = [];

    // Reset form ship fields to default values
    this.invoiceForm.patchValue({
      ship_id: '',
      shipName: 'GALAXY',
      ageName: 'MOL',
      voyageNumber: '01514',
      sailingDate: '2025-10-13',
      sailingPort: 'Yokohama',
      destinationPort: 'Auckland',
      destinationCountry: 'New Zealand',
      arrivalDate: '2025-11-08'
    });

    // Also reset static data for backward compatibility
    this.staticData.shipName = 'GALAXY';
    this.staticData.ageName = 'MOL';
    this.staticData.voyageNumber = '01514';
    this.staticData.sailingDate = '2025-10-13';
    this.staticData.sailingPort = 'Yokohama';
    this.staticData.destinationPort = 'Auckland';
    this.staticData.destinationCountry = 'New Zealand';
    this.staticData.arrivalDate = '2025-11-08';
  }

  onShipInputFocus(): void {
    if (this.shipSearchResults.length > 0) {
      this.showShipDropdown = true;
    }
  }

  onShipInputBlur(): void {
    // Delay hiding dropdown to allow for click events
    setTimeout(() => {
      this.showShipDropdown = false;
    }, 200);
  }

  getShipDisplayName(ship: any): string {
    return `${ship.ship_id}, ${ship.name} - ${ship.company} (${ship.voyage_no})`;
  }

  // User search methods for Consignee
  private setupUserSearchDebounce(): void {
    this.userSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      if (searchTerm.length >= 2) {
        this.searchUsers(searchTerm);
      } else {
        this.userSearchResults = [];
        this.showUserDropdown = false;
      }
    });
  }

  onUserSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.userSearchTerm = target.value;
    this.selectedUser = null;

    if (this.userSearchTerm.length >= 2) {
      this.userSearchSubject.next(this.userSearchTerm);
      this.showUserDropdown = true;
    } else {
      this.showUserDropdown = false;
      this.userSearchResults = [];
    }
  }

  private searchUsers(searchTerm: string): void {
    this.isUserSearching = true;
    this.subscriptions.push(
      this._userService.GetData('1', searchTerm, { paginate: false }).subscribe({
        next: (response: any) => {
          this.isUserSearching = false;
          if (response.status && response.data) {
            const users = Array.isArray(response.data) ? response.data : response.data.data || [];
            this.userSearchResults = users;
            this.showUserDropdown = this.userSearchResults.length > 0;
          } else {
            this.userSearchResults = [];
            this.showUserDropdown = false;
          }
        },
        error: (error: any) => {
          this.isUserSearching = false;
          console.error('Error searching users:', error);
          this.userSearchResults = [];
          this.showUserDropdown = false;
        }
      })
    );
  }

  selectUser(user: User): void {
    this.selectedUser = user;
    this.userSearchTerm = user.name || '';
    this.showUserDropdown = false;
    this.userSearchResults = [];

    // Update form with selected user information
    this.invoiceForm.patchValue({
      consigneeId: user._id || '',
      consignee: user.company || '',
      consigneeName: user.name || '',
      consigneeEmail: user.email || '',
      consigneePhone: user.phone || '',
      consigneeAddress: user.address || '',
      consigneeCity: user.city || ''
    });

    // If "Same as Consignee" is checked, also update notify party fields
    if (this.invoiceForm.get('sameAsConsignee')?.value) {
      this.updateNotifyPartyFromConsignee();
    }
  }

  clearUserSearch(): void {
    this.userSearchTerm = '';
    this.selectedUser = null;
    this.showUserDropdown = false;
    this.userSearchResults = [];

    // Clear consignee fields
    this.invoiceForm.patchValue({
      consigneeId: '',
      consignee: '',
      consigneeName: '',
      consigneeEmail: '',
      consigneePhone: '',
      consigneeAddress: '',
      consigneeCity: ''
    });

    // If "Same as Consignee" is checked, also clear notify party fields
    if (this.invoiceForm.get('sameAsConsignee')?.value) {
      this.clearNotifyPartyFields();
    }
  }

  onUserInputFocus(): void {
    if (this.userSearchResults.length > 0) {
      this.showUserDropdown = true;
    }
  }

  onUserInputBlur(): void {
    // Delay hiding dropdown to allow for click events
    setTimeout(() => {
      this.showUserDropdown = false;
    }, 200);
  }

  getUserDisplayName(user: User): string {
    const name = user.name || '';
    const company = user.company || '';
    const email = user.email || '';
    return `${name}${company ? ' - ' + company : ''}${email ? ' (' + email + ')' : ''}`;
  }

  // Handle "Same as Consignee" checkbox change
  onSameAsConsigneeChange(): void {
    const sameAsConsignee = this.invoiceForm.get('sameAsConsignee')?.value;

    if (sameAsConsignee) {
      this.updateNotifyPartyFromConsignee();
    } else {
      this.clearNotifyPartyFields();
    }
  }

  private updateNotifyPartyFromConsignee(): void {
    const consigneeData = {
      notifyPartyCompany: this.invoiceForm.get('consignee')?.value || '',
      notifyPartyName: this.invoiceForm.get('consigneeName')?.value || '',
      notifyPartyEmail: this.invoiceForm.get('consigneeEmail')?.value || '',
      notifyPartyPhone: this.invoiceForm.get('consigneePhone')?.value || '',
      notifyPartyAddress: this.invoiceForm.get('consigneeAddress')?.value || '',
      notifyPartyCity: this.invoiceForm.get('consigneeCity')?.value || ''
    };

    this.invoiceForm.patchValue(consigneeData);
  }

  private clearNotifyPartyFields(): void {
    this.invoiceForm.patchValue({
      notifyPartyCompany: '',
      notifyPartyName: '',
      notifyPartyEmail: '',
      notifyPartyPhone: '',
      notifyPartyAddress: '',
      notifyPartyCity: ''
    });
  }

  cancelInvoice(): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to cancel this invoice. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, cancel it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performCancelInvoice();
      }
    });
  }

  private performCancelInvoice(): void {
    this.isLoading = true;

    this._invoiceService.UpdateData(this.selectedInvoice?._id || "", { status: 'cancelled' }).subscribe({
      next: (response: any) => {
        this.isLoading = false;

        Swal.fire({
          title: 'Cancelled!',
          text: 'Invoice has been cancelled successfully.',
          icon: 'success',
          confirmButtonText: 'OK'
        }).then(() => {
          // Close the modal
          this.closeModal();

          // Emit event to refresh the list
          this.invoiceCancelled.emit();
        });
      },
      error: (error) => {
        console.error('Error cancelling invoice:', error);
        this.isLoading = false;

        Swal.fire({
          title: 'Error!',
          text: error.error?.message || 'An error occurred while cancelling the invoice.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    });
  }

  // Tooltip methods for allocations
  showAllocationTooltip(event: MouseEvent): void {
    const target = event.currentTarget as HTMLElement;
    const tooltip = target.querySelector('.allocation-details-popup') as HTMLElement;

    if (!tooltip) return;

    // Hide any existing tooltip
    this.hideAllocationTooltip();

    // Show the tooltip
    tooltip.style.display = 'block';
    this.currentTooltip = tooltip;

    // Position the tooltip
    this.positionTooltip(target, tooltip);
  }

  hideAllocationTooltip(): void {
    if (this.currentTooltip) {
      this.currentTooltip.style.display = 'none';
      this.currentTooltip = null;
    }
  }

  private positionTooltip(trigger: HTMLElement, tooltip: HTMLElement): void {
    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    let top = triggerRect.bottom + scrollTop + 8;
    let left = triggerRect.left + scrollLeft + (triggerRect.width / 2) - (tooltipRect.width / 2);

    // Adjust horizontal position if tooltip goes outside viewport
    if (left < scrollLeft + 10) {
      left = scrollLeft + 10;
    } else if (left + tooltipRect.width > scrollLeft + viewportWidth - 10) {
      left = scrollLeft + viewportWidth - tooltipRect.width - 10;
    }

    // Adjust vertical position if tooltip goes outside viewport
    if (top + tooltipRect.height > scrollTop + viewportHeight - 10) {
      // Show above the trigger
      top = triggerRect.top + scrollTop - tooltipRect.height - 8;
    }

    // Ensure tooltip doesn't go above the viewport
    if (top < scrollTop + 10) {
      top = scrollTop + 10;
    }

    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
  }

  // Helper methods for allocations
  sumOfAllocations(allocations: any[], currency_type: string = "usd"): number {
    let sum = 0;
    const amount_prop = currency_type === "usd" ? "amount_in_usd" : "amount_in_jpy";

    allocations.forEach((allocation: any) => {
      sum += +allocation[amount_prop] ? +allocation[amount_prop] : 0;
    });
    return sum?+sum.toFixed(2):0;
  }

  averageOfRate(allocations: any[]): string {
    let sum = 0;
    allocations.forEach((allocation: any) => {
      sum += allocation.ex_rate ? (+allocation.ex_rate) : 0;
    });
    return (sum / allocations.length).toFixed(2);
  }

  // Search invoice by stock number
  searchInvoiceByStockNumber(): void {
    const stockNumber = this.invoiceForm.get('stockNumberSearch')?.value;

    if (!stockNumber || stockNumber.trim() === '') {
      Swal.fire({
        title: 'Warning!',
        text: 'Please enter a stock number to search.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    this.isSearchingInvoice = true;

    // Call the invoice GetData API with stock_number as query param
    this.subscriptions.push(
      this._invoiceService.GetData('1', '', { stock_number: stockNumber.trim() }).subscribe({
        next: (response: any) => {
          this.isSearchingInvoice = false;

          if (response.status && response.data && response.data.data && response.data.data.length > 0) {
            // Pick the first invoice from the array
            const invoice = response.data.data[0];
            this.selectedInvoice = invoice;

            // Reinitialize the form with the loaded invoice data
            this.initializeForm();

            Swal.fire({
              title: 'Success!',
              text: 'Invoice loaded successfully.',
              icon: 'success',
              confirmButtonText: 'OK',
              timer: 2000
            });
          } else {
            Swal.fire({
              title: 'Not Found!',
              text: 'No invoice found with the provided stock number.',
              icon: 'info',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isSearchingInvoice = false;
          console.error('Error searching invoice:', error);

          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while searching for the invoice.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }
}
